import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getAuthInfo } from '@/utils/auth'
import {
  COUPON_STATUS_LABEL,
  getCouponHistoryPaged,
  postCouponCancelUse,
  toCouponHistoryUiStatus
} from '@/api/coupon'
import type { CouponItemDto, CouponListData } from '@/api/coupon'
import type { MemberDashboardState, MemberProfile } from '@/types/member'
import styles from './CouponHistory.module.css'

const BLOCKED_MEMBER_MESSAGE = '차단된 회원입니다. 적립·사용이 불가합니다.'

/** 쿠폰 상태 */
type CouponStatus = 'active' | 'used' | 'expired' | 'cancelled' | 'suspended'

/** 쿠폰 내역 한 건 */
interface CouponItem {
  id: string
  issuedDate: string
  status: CouponStatus
  statusLabel: string
  name: string
  description: string
  useDateLabel: string
  /** 사용 취소 가능 기간 판단용 */
  usedAt?: string
  /** 사용 취소 API용(목록의 usedOrderNo 또는 transactionId) */
  usedOrderNo?: string
}

const ITEMS_PER_PAGE = 20

const STATUS_LABEL: Record<CouponStatus, string> = {
  active: '발급완료',
  used: '사용완료',
  expired: '만료',
  cancelled: '취소됨',
  suspended: '사용정지'
}

function formatYmdDot(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

/** 사용일(usedAt) 이후 7일째 날 자정(23:59:59)까지 취소 가능 */
function isWithinCancelWindow(usedAt: string): boolean {
  const used = new Date(usedAt)
  if (Number.isNaN(used.getTime())) return false

  const deadline = new Date(used)
  deadline.setDate(deadline.getDate() + 7)
  deadline.setHours(23, 59, 59, 999)

  return Date.now() <= deadline.getTime()
}

function toCouponItem(dto: CouponItemDto): CouponItem {
  const expireAtMs = new Date(dto.expireAt).getTime()
  const isExpiredByTime =
    !Number.isNaN(expireAtMs) &&
    Date.now() > expireAtMs &&
    dto.status !== 'USED' &&
    dto.status !== 'CANCELED' &&
    dto.status !== 'SUSPENDED'

  const uiStatus = isExpiredByTime ? 'expired' : toCouponHistoryUiStatus(dto.status)
  const issuedDate = formatYmdDot(dto.issuedAt)

  const useDateLabel = dto.usedAt
    ? `사용: ${formatYmdDot(dto.usedAt)}`
    : dto.expireAt
      ? `만료: ${formatYmdDot(dto.expireAt)}`
      : ''

  const usedOrderNo = [dto.usedOrderNo, dto.transactionId]
    .map((s) => (s != null ? String(s).trim() : ''))
    .find((s) => s !== '')

  return {
    id: dto.couponCode,
    issuedDate,
    status: uiStatus,
    statusLabel: isExpiredByTime
      ? '만료'
      : COUPON_STATUS_LABEL[dto.status] ?? STATUS_LABEL[uiStatus],
    name: dto.couponName,
    description: dto.couponName,
    useDateLabel,
    usedAt: dto.usedAt,
    usedOrderNo
  }
}

export default function CouponHistory() {
  const navigate = useNavigate()
  const location = useLocation()

  const [member, setMember] = useState<MemberProfile | null>(null)
  const [isBlocked, setIsBlocked] = useState(false)
  const [coupons, setCoupons] = useState<CouponItem[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageData, setPageData] = useState<CouponListData | null>(null)
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [cancellingCouponCode, setCancellingCouponCode] = useState<string | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [])

  const goBack = () => {
    navigate('/dashboard/member', { state: location.state })
  }

  const goToPage = (page: number | 'prev' | 'next') => {
    const totalPages = pageData?.totalPages ?? 1
    if (page === 'prev') {
      setCurrentPage((p) => (p > 1 ? p - 1 : p))
    } else if (page === 'next') {
      setCurrentPage((p) => (p < totalPages ? p + 1 : p))
    } else {
      setCurrentPage(page)
    }
  }

  const loadCouponHistory = useCallback(async (page: number) => {
    if (!member) return
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    if (!projectCode || !shopCode) {
      showToast('매장 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }

    setLoading(true)
    try {
      const data = await getCouponHistoryPaged({
        projectCode,
        shopCode,
        customerCode: member.customerCode,
        issuedShopCode: shopCode,
        recentDays: 93,
        page: Math.max(0, page - 1),
        size: ITEMS_PER_PAGE,
        sort: 'issuedAt,DESC'
      })
      setPageData(data)
      setCoupons(Array.isArray(data.coupons) ? data.coupons.map(toCouponItem) : [])
    } catch (e) {
      showToast(e instanceof Error ? e.message : '쿠폰 내역을 불러올 수 없습니다.', 'error')
      setPageData(null)
      setCoupons([])
    } finally {
      setLoading(false)
    }
  }, [member, showToast])

  const cancelCoupon = async (couponCode: string, usedOrderNo: string) => {
    if (isBlocked) {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }
    if (
      !window.confirm(
        '쿠폰 사용을 취소하시겠습니까?\n취소된 쿠폰은 다시 사용 가능한 상태로 변경됩니다.'
      )
    ) {
      return
    }
    if (!member) return
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    if (!projectCode || !shopCode) {
      showToast('매장 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    setCancellingCouponCode(couponCode)
    try {
      await postCouponCancelUse(projectCode, shopCode, couponCode, {
        customerCode: member.customerCode,
        usedOrderNo
      })
      showToast('쿠폰 사용이 취소되었습니다.', 'success')
      await loadCouponHistory(currentPage)
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : '쿠폰 사용 취소에 실패했습니다.',
        'error'
      )
    } finally {
      setCancellingCouponCode(null)
    }
  }

  useEffect(() => {
    const state = location.state as MemberDashboardState | null
    setMember(state?.member ?? null)
    setIsBlocked(state?.status === 'BLOCKED')
    setCurrentPage(1)
    setPageData(null)
    setCoupons([])
  }, [location.state])

  useEffect(() => {
    loadCouponHistory(currentPage)
  }, [currentPage, loadCouponHistory])

  const statusClass = (status: CouponStatus) => {
    switch (status) {
      case 'active':
        return styles.couponStatusActive
      case 'used':
        return styles.couponStatusUsed
      case 'expired':
        return styles.couponStatusExpired
      case 'cancelled':
        return styles.couponStatusCancelled
      case 'suspended':
        return styles.couponStatusSuspended
      default:
        return ''
    }
  }

  if (!member) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
              ←
            </button>
            <h1 className={styles.pageTitle}>쿠폰 내역</h1>
          </div>
        </header>
        <main className={styles.mainContainer}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🎟️</div>
            <div className={styles.emptyMessage}>회원 정보가 없습니다.</div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
            ←
          </button>
          <h1 className={styles.pageTitle}>쿠폰 내역</h1>
        </div>
      </header>

      <main className={styles.mainContainer}>
        <section className={styles.memberInfoCard}>
          <div className={styles.memberHeader}>
            <div className={styles.memberDetails}>
              <div className={styles.memberName}>{member.nickname}</div>
            <div className={styles.memberBarcode}>{member.customerCode}</div>
            </div>
          </div>
        </section>

        <section className={styles.historySection}>
          <h2 className={styles.sectionTitle}>쿠폰 내역</h2>

          {loading && coupons.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyMessage}>쿠폰 내역을 불러오는 중입니다.</div>
            </div>
          ) : coupons.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🎟️</div>
              <div className={styles.emptyMessage}>쿠폰 내역이 없습니다.</div>
            </div>
          ) : (
            <>
              <div className={styles.couponList}>
                {coupons.map((item) => {
                  const usedOrderNoRef = item.usedOrderNo
                  return (
                  <div key={item.id} className={styles.couponItem}>
                    <div className={styles.couponContent}>
                      <div className={styles.couponHeader}>
                        <span className={styles.couponDate}>{item.issuedDate}</span>
                        <span className={`${styles.couponStatus} ${statusClass(item.status)}`}>
                          {item.statusLabel}
                        </span>
                      </div>
                      <div className={styles.couponName}>{item.name}</div>
                      <div className={styles.couponDescription}>{item.description}</div>
                    </div>
                    <div className={styles.couponActions}>
                      {item.status === 'used' &&
                        !isBlocked &&
                        usedOrderNoRef != null &&
                        item.usedAt != null &&
                        isWithinCancelWindow(item.usedAt) && (
                        <button
                          type="button"
                          className={styles.cancelButton}
                          onClick={() => void cancelCoupon(item.id, usedOrderNoRef)}
                          disabled={cancellingCouponCode !== null}
                        >
                          {cancellingCouponCode === item.id ? '처리 중...' : '사용 취소'}
                        </button>
                      )}
                      <span className={styles.useDate}>{item.useDateLabel}</span>
                    </div>
                  </div>
                  )
                })}
              </div>

              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => goToPage('prev')}
                  disabled={currentPage === 1}
                  aria-label="이전 페이지"
                >
                  ←
                </button>
                {Array.from({ length: pageData?.totalPages ?? 1 }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.pageButton} ${currentPage === p ? styles.active : ''}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => goToPage('next')}
                  disabled={currentPage === (pageData?.totalPages ?? 1)}
                  aria-label="다음 페이지"
                >
                  →
                </button>
                <div className={styles.pageInfo}>
                  총 {pageData?.totalCount ?? 0}건 (페이지 {currentPage}/{pageData?.totalPages ?? 1})
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="alert"
        >
          {toast.message}
        </div>
      )}
    </div>
  )
}
